import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useUpdateEnrollFace } from './api';

// Two source modes for getting a photo into the same upload pipeline.
const MODE_UPLOAD = 'upload';
const MODE_CAMERA = 'camera';

const EmployeeFaceEnroll = ({ employeeId, employeeName, onEnrolled }) => {
  const [mode, setMode] = useState(MODE_UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const { updateFaceEnroll, isUpdating } = useUpdateEnrollFace();

  // ---------- shared helpers ----------

  const resetSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setMessage(null);
  };

  const validateAndSetFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be under 5MB.' });
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMessage(null);
  };

  // ---------- upload-from-device mode ----------

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    validateAndSetFile(file);
  };

  // ---------- live camera mode ----------

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch (err) {
      setCameraError(
        err?.name === 'NotAllowedError'
          ? 'Camera permission was denied. Please allow camera access and try again.'
          : 'Could not access the camera. Please check it is connected and not in use by another app.',
      );
      setCameraReady(false);
    }
  }, []);

  // Start/stop the camera stream as the mode changes, and always clean up on unmount.
  useEffect(() => {
    if (mode === MODE_CAMERA) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [mode, startCamera, stopCamera]);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setMessage({
            type: 'error',
            text: 'Could not capture photo. Please try again.',
          });
          return;
        }
        // Wrap the blob as a File so it behaves identically to a file-picker selection
        // (same object shape the rest of the component, and your backend, already expect).
        const file = new File(
          [blob],
          `${employeeId}-capture-${Date.now()}.jpg`,
          {
            type: 'image/jpeg',
          },
        );
        validateAndSetFile(file);
      },
      'image/jpeg',
      0.92,
    );
  };

  const retakePhoto = () => {
    resetSelection();
  };

  // ---------- upload to backend (same for both modes) ----------

  const handleUpload = () => {
    if (!selectedFile) {
      setMessage({
        type: 'error',
        text: 'Please choose or capture a photo first.',
      });
      return;
    }

    const formData = new FormData();
    formData.append('employee_id', employeeId);
    formData.append('photo', selectedFile);

    updateFaceEnroll(formData)
      .then((response) => {
        setMessage({
          type: 'success',
          text:
            response?.data?.message ||
            response?.message ||
            'Face enrolled successfully.',
        });
        onEnrolled?.(response?.data ?? response);
        resetSelection();
        if (fileInputRef.current) fileInputRef.current.value = '';
      })
      .catch((error) => {
        const errMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Something went wrong. Please try again.';
        setMessage({ type: 'error', text: errMessage });
      });
  };

  const switchMode = (newMode) => {
    if (newMode === mode) return;
    resetSelection();
    setMode(newMode);
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Enroll Face — {employeeName}</h3>

      {/* Mode toggle */}
      <div style={styles.tabRow}>
        <button
          type="button"
          onClick={() => switchMode(MODE_UPLOAD)}
          style={{
            ...styles.tabButton,
            ...(mode === MODE_UPLOAD ? styles.tabButtonActive : {}),
          }}
        >
          Upload Photo
        </button>
        <button
          type="button"
          onClick={() => switchMode(MODE_CAMERA)}
          style={{
            ...styles.tabButton,
            ...(mode === MODE_CAMERA ? styles.tabButtonActive : {}),
          }}
        >
          Live Capture
        </button>
      </div>

      {mode === MODE_UPLOAD && (
        <>
          <div
            style={{ ...styles.previewBox, cursor: 'pointer' }}
            onClick={openFilePicker}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') openFilePicker();
            }}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="Preview" style={styles.previewImage} />
            ) : (
              <span style={styles.previewPlaceholder}>
                Click to select a photo
              </span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </>
      )}

      {mode === MODE_CAMERA && (
        <div style={styles.previewBox}>
          {previewUrl ? (
            // Captured shot preview — camera stream is paused/hidden behind it
            <img
              src={previewUrl}
              alt="Captured preview"
              style={styles.previewImage}
            />
          ) : cameraError ? (
            <span
              style={{
                ...styles.previewPlaceholder,
                padding: '0 16px',
                textAlign: 'center',
              }}
            >
              {cameraError}
            </span>
          ) : (
            <video
              ref={videoRef}
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: cameraReady ? 'block' : 'none',
              }}
            />
          )}
          {!previewUrl && !cameraReady && !cameraError && (
            <span style={styles.previewPlaceholder}>Starting camera…</span>
          )}
        </div>
      )}

      {/* Hidden canvas used only to grab a still frame from the video */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {mode === MODE_CAMERA && !previewUrl && cameraReady && (
        <button
          type="button"
          onClick={capturePhoto}
          style={{ ...styles.button, marginBottom: 12 }}
        >
          Capture Photo
        </button>
      )}

      {mode === MODE_CAMERA && previewUrl && (
        <button
          type="button"
          onClick={retakePhoto}
          style={{
            ...styles.button,
            ...styles.secondaryButton,
            marginBottom: 12,
          }}
        >
          Retake Photo
        </button>
      )}

      <button
        onClick={handleUpload}
        disabled={isUpdating || !selectedFile}
        style={{
          ...styles.button,
          ...(isUpdating || !selectedFile ? styles.buttonDisabled : {}),
        }}
      >
        {isUpdating ? 'Enrolling...' : 'Upload & Enroll Face'}
      </button>

      {message && (
        <div
          style={{
            ...styles.messageBox,
            ...(message.type === 'success'
              ? styles.messageSuccess
              : styles.messageError),
          }}
        >
          {message.text}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: 360,
    padding: 20,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    fontFamily: 'sans-serif',
  },
  title: { marginTop: 0, marginBottom: 16, fontSize: 16 },
  tabRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  tabButton: {
    flex: 1,
    padding: '8px 12px',
    fontSize: 13,
    borderRadius: 6,
    border: '1px solid #ddd',
    backgroundColor: '#f5f5f5',
    color: '#555',
    cursor: 'pointer',
  },
  tabButtonActive: {
    backgroundColor: '#2E7D32',
    borderColor: '#2E7D32',
    color: '#fff',
  },
  previewBox: {
    width: '100%',
    height: 240,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    overflow: 'hidden',
  },
  previewImage: { width: '100%', height: '100%', objectFit: 'cover' },
  previewPlaceholder: { color: '#999', fontSize: 14 },
  button: {
    width: '100%',
    padding: '10px 16px',
    backgroundColor: '#2E7D32',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer',
  },
  secondaryButton: {
    backgroundColor: '#fff',
    color: '#2E7D32',
    border: '1px solid #2E7D32',
  },
  buttonDisabled: { backgroundColor: '#a5a5a5', cursor: 'not-allowed' },
  messageBox: {
    marginTop: 12,
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: 13,
  },
  messageSuccess: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
  messageError: { backgroundColor: '#ffebee', color: '#c62828' },
};

export default EmployeeFaceEnroll;
